import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { ICustomerType } from '../../constants/customer';
import { ITranslations } from '../../interface';
import { IMatterType } from '../../interface/matter-types';
import { useSelectMatterRequestType } from '../../hooks/useSelectMatterRequestType';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import NoData from '../dashboard/noData';
import { GeneralEmpty } from '../icons';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useEffect, useMemo } from 'react';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import SortSelector from '../sortPopover/sortSelector';
import { ISortOption } from '../../interface/common';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

// Define a common interface for selectable items
interface SelectableItem {
  id: ICustomerType;
  name: string;
  translations?: ITranslations[];
}

interface RadioSelectionProps {
  selectionData: SelectableItem[];
  currentValue: ICustomerType | string | undefined;
  isCustomerTypeSelection: boolean;
  matterRequestTypeList: IMatterType[] | undefined;
  handleSelectCustomerType: (customerType: ICustomerType) => void;
  handleSelectMatterRequestType: (matterRequestType: IMatterType) => void;
  appLanguage: string;
}

const RadioSelection: React.FC<RadioSelectionProps> = ({
  selectionData,
  currentValue,
  isCustomerTypeSelection,
  matterRequestTypeList,
  handleSelectCustomerType,
  handleSelectMatterRequestType,
  appLanguage,
}) => {
  if (!selectionData || selectionData.length === 0) {
    return null;
  }
  return (
    <RadioGroup data-testid="radio-group" className="d-flex fd-column gap-10" value={currentValue}>
      {selectionData.map((item: SelectableItem) => (
        <Radio
          key={item.id}
          data-testid={`radio-${item.name}`}
          value={item.id}
          onClick={() => {
            if (isCustomerTypeSelection) {
              handleSelectCustomerType(item.id);
              return;
            }
            const matterType = matterRequestTypeList?.find((mrt) => mrt.id === item.id);
            if (!matterType) {
              return;
            }
            handleSelectMatterRequestType(matterType);
          }}
        >
          <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
            {isCustomerTypeSelection
              ? item.name
              : getTranslationFromTranslatableEntity(item.translations, appLanguage)}
          </Text>
        </Radio>
      ))}
    </RadioGroup>
  );
};

const FullPageLoader: React.FC = () => <SkeletonOptionList />;

// New component for empty state
const EmptyState: React.FC<{ isCustomerTypeSelection: boolean; t: (key: string) => string }> = ({
  isCustomerTypeSelection,
  t,
}) => (
  <div className="flex-grow d-flex justify-center items-center">
    <NoData
      header={
        isCustomerTypeSelection
          ? t('matterRequest:noCustomerTypes')
          : t('matterRequest:noMatterRequestTypes')
      }
      subHeader={t('common:tryChangingSearchCriteria')}
      icon={<GeneralEmpty />}
    />
  </div>
);

// New component for inline fetching indicator
const InlineFetcher: React.FC = () => <Spin data-testid="small-indicator" size="small" />;

const SelectMatterRequestType = () => {
  const {
    t,
    appLanguage,
    isCustomerTypeSelection,
    setSearchValue,
    selectionData,
    currentValue,
    matterRequestTypeList,
    handleSelectCustomerType,
    handleSelectMatterRequestType,
    paginationRef,
    hasNextPage,
    isLoading,
    isFetching,
    activeSort,
    setActiveSort,
    refreshMatterRequestTypeList,
  } = useSelectMatterRequestType();
  const { setTitle } = usePage();
  const { goBack } = useNavigationStack();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    // manageMatterRequest.tsx has no discard model — this mirrors its own handleBack
    // (reset + leave). When embedded in the mail flow (transactionMail.tsx) it's not a
    // separate screen on the stack, so only one level needs to be skipped there vs two
    // for the standalone create/edit matter request route.
    updateManageMatterRequest(null, true);
    goBack(undefined, matterRequest?.isFromMailOrAppointment ? 1 : 2);
  };
  const matterRequestTypeSortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'name', label: t('matterRequest:type'), type: 'text', defaultDirection: 'ASC' },
    ],
    [t],
  );
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={
          isCustomerTypeSelection
            ? t('matterRequest:selectCustomerOption')
            : t('matterRequest:selectType')
        }
        showCross
      />,
    );
  }, [isCustomerTypeSelection]);
  return (
    <CollapsibleListLayout
      testId="matter-request-type-selection"
      header={
        !isCustomerTypeSelection && (
          <SearchInputWithFilters
            searchTitle={t('matterRequest:searchMatterRequestTypes')}
            handleSearch={(val) => {
              setSearchValue(val);
            }}
            handleRefresh={refreshMatterRequestTypeList}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={matterRequestTypeSortOptions}
              activeSort={activeSort}
              onApply={setActiveSort}
            />
          </SearchInputWithFilters>
        )
      }
    >
      <RadioSelection
        selectionData={selectionData}
        currentValue={currentValue}
        isCustomerTypeSelection={isCustomerTypeSelection}
        matterRequestTypeList={matterRequestTypeList}
        handleSelectCustomerType={handleSelectCustomerType}
        handleSelectMatterRequestType={handleSelectMatterRequestType}
        appLanguage={appLanguage}
      />
      {!isCustomerTypeSelection && hasNextPage && (
        <div ref={paginationRef} className="loading-indicator" />
      )}
      {/* Conditional rendering of the new components */}
      {isLoading && <FullPageLoader />}
      {!selectionData || (selectionData.length === 0 && !isLoading) ? (
        <EmptyState isCustomerTypeSelection={isCustomerTypeSelection} t={t} />
      ) : null}
      {!isCustomerTypeSelection && isFetching && !isLoading && <InlineFetcher />}
    </CollapsibleListLayout>
  );
};

export default SelectMatterRequestType;
